import { hashKey } from '@tanstack/react-query'
import { apiGet } from './api'
import { sessionKeys } from './queryKeys/session'
import { parseCurrentUserResponse } from './webApiContracts'
import { identifyUser, resetTrackingUser, trackActions } from '../tracking/trackingScripts'
import { classifyActor } from '../tracking/actorClass'
import { addBreadcrumb, reportError } from '../tracking/sentry'
import {
  completeInsightsMeasure,
  INSIGHTS_PERFORMANCE,
  startInsightsMeasure,
} from '../performance/insightsPerformance'

/**
 * Writes for the signed-in user, who lives in the React Query cache under
 * `sessionKeys.currentUser()`. `hooks/useCurrentUser` is the only reader.
 *
 * The user changes only through explicit writes (session bootstrap, login and
 * sign-out here; preference updates in `./currentUserPreferences`), so nothing
 * refetches on its own.
 */

/** Signed-out sessions store an empty user so the app shows the login screen. */
export const EMPTY_USER = {}

const currentUserKey = sessionKeys.currentUser()
const currentUserHash = hashKey(currentUserKey)

const isProduction = () => process.env.NODE_ENV === 'production'

/**
 * Identify the user with the tracking tools, then record the session event.
 * Runs before the user reaches the cache, so nothing tracked by the app can
 * carry the previous identity. The actor class reads the raw response: the
 * Web API parser defaults missing flags to false, which would misclassify.
 */
const identifyCurrentUser = (user, rawUser) => {
  const {
    id,
    firstName,
    lastName,
    email,
    isAdmin,
    isInternal,
    accountId,
    accountName,
    isDemoUser,
    isResidentEngagementUser,
    isReadOnlyUser,
    isDataOnlyUser,
    isTasksOnlyUser,
    isAutomatedSurveyUser,
  } = user
  try {
    identifyUser(
      id,
      {
        userId: id,
        actorClass: classifyActor(user, rawUser),
        name: `${firstName} ${lastName}`,
        email,
        isAdmin,
        isInternal,
        accountId,
        accountName,
        isReadOnlyUser,
        isDataOnlyUser,
        isTasksOnlyUser,
        isAutomatedSurveyUser,
        isResidentEngagementUser,
        isDemoUser,
      },
      isProduction(),
    )
    // The session event the Redux SET_USER action used to produce. Trackers
    // attach only the fixed actor context, never the user object.
    trackActions({ type: 'SET_USER' })
  } catch (trackingError) {
    console.error('Unable to identify the authenticated user for tracking:', trackingError)
  }
}

const identifyAndParse = (rawUser) => {
  const user = parseCurrentUserResponse(rawUser)
  identifyCurrentUser(user, rawUser)
  return user
}

const resetTracking = () => {
  try {
    resetTrackingUser(isProduction())
  } catch (trackingError) {
    console.error('Unable to reset tracking:', trackingError)
  }
}

// Reporting must never change the auth outcome.
const safely = (report) => {
  try {
    report()
  } catch (trackingError) {
    console.error('Unable to report auth failure:', trackingError)
  }
}

const fetchCurrentUserResponse = async ({ signal }) => {
  const measurement = startInsightsMeasure(INSIGHTS_PERFORMANCE.sessionRequest)
  let data
  try {
    data = await apiGet('/api/user', {
      signal,
      headers: { Pragma: 'no-cache' },
      withCredentials: true,
    })
  } catch (error) {
    completeInsightsMeasure(measurement, 'error')
    throw error
  }
  completeInsightsMeasure(measurement, 'success')
  return identifyAndParse(data)
}

/**
 * Resolve the signed-in user into the cache and return it.
 *
 * With `bootstrapUser` (the login response can carry one) the cache is seeded
 * directly and no request is made. Otherwise `GET /api/user` runs once; a
 * concurrent call shares the in-flight request. Neither path retries.
 *
 * A 401 means signed out: the cache holds `EMPTY_USER` so the app shows login,
 * a breadcrumb is recorded, and the promise resolves to `null`. Any other error
 * (a 500, a timeout) says nothing about the session, so it is reported and the
 * promise rejects. The caller shows a retryable error instead of the login form.
 *
 * @param {import('@tanstack/react-query').QueryClient} queryClient
 * @param {Record<string, unknown> | null} [bootstrapUser]
 * @returns {Promise<Record<string, any> | null>}
 */
export const establishCurrentUser = async (queryClient, bootstrapUser) => {
  try {
    if (bootstrapUser !== null && bootstrapUser !== undefined) {
      const user = identifyAndParse(bootstrapUser)
      queryClient.setQueryData(currentUserKey, user)
      return user
    }
    const user = await queryClient.fetchQuery({
      queryKey: currentUserKey,
      queryFn: fetchCurrentUserResponse,
      staleTime: 0,
      retry: false,
    })
    return user
  } catch (error) {
    // Whoever was identified before is not confirmed any more.
    resetTracking()
    if (error?.response?.status === 401) {
      safely(() =>
        addBreadcrumb('auth.unauthorized', {
          message: 'Unauthorized or no user, redirected to login',
          route: '/api/user',
        }),
      )
      queryClient.setQueryData(currentUserKey, EMPTY_USER)
      return null
    }
    safely(() =>
      reportError(error, {
        context: 'fetchCurrentUser',
        domain: 'auth',
        surface: 'session_bootstrap',
        route: '/api/user',
      }),
    )
    console.error(error)
    throw error
  }
}

/**
 * Sign-out: reset the tracking identity, empty the user first so mounted readers re-render signed out, then
 * drop every cached server-state entry.
 */
export const clearCurrentUserSession = (queryClient) => {
  resetTracking()
  queryClient.setQueryData(currentUserKey, EMPTY_USER)
  queryClient.clear()
}

/**
 * The 401 interceptor: drop all server state except the user. The app keeps
 * rendering behind the "please refresh" toast, as it did when the user lived in
 * Redux, instead of half the screen losing the user mid-render.
 */
export const clearServerStateExceptCurrentUser = (queryClient) =>
  queryClient.removeQueries({ predicate: (query) => query.queryHash !== currentUserHash })
